import { useState, useEffect, useCallback } from "react";
import { Lightbulb, ArrowRight } from "@phosphor-icons/react";
import API from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import KbAskBox from "./KbAskBox";
import { renderKbContent, KbThumb } from "./kbMarkdown";
import { KbWalkthrough } from "./KbWalkthrough";

/**
 * Embedded, role-locked knowledge base list + article reader.
 * Used by the Supplier Portal Help tab, the Client Portal /portal/help page and the
 * operative /my-work/help page. `role` is one of vendor | client | in_house.
 */
export default function PortalKnowledgeBase({ role, intro, placeholder = "Search help articles...", testIdPrefix = "portal-help" }) {
  const [data, setData] = useState({ sections: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openSlug, setOpenSlug] = useState(null);
  const [article, setArticle] = useState(null);
  const [voted, setVoted] = useState(null);
  const tid = (s) => `${testIdPrefix}-${s}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await API.get(`/kb/articles?role=${role}${query ? `&q=${encodeURIComponent(query)}` : ""}`);
      setData(d);
    } catch {
      toast.error("Failed to load help articles");
    } finally {
      setLoading(false);
    }
  }, [role, query]);
  useEffect(() => { load(); }, [load]);

  const openArt = async (slug) => {
    setOpenSlug(slug);
    setArticle(null);
    setVoted(null);
    try {
      const { data: d } = await API.get(`/kb/articles/${slug}`);
      setArticle(d);
    } catch {
      toast.error("Couldn't load article");
      setOpenSlug(null);
    }
  };

  const vote = async (helpful) => {
    if (voted) return;
    setVoted(helpful ? "yes" : "no");
    try {
      await API.post(`/kb/articles/${openSlug}/feedback`, { helpful });
      toast.success("Thanks for the feedback!");
    } catch {
      toast.error("Couldn't record feedback");
    }
  };

  if (openSlug) {
    return (
      <div className="max-w-3xl" data-testid={tid("article-view")}>
        <Button variant="ghost" size="sm" onClick={() => { setOpenSlug(null); setArticle(null); }} className="mb-3" data-testid={tid("back")}>
          ← Back to help
        </Button>
        {!article ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
        ) : (
          <div className="border border-border rounded-lg p-5 bg-card">
            <h2 className="text-lg font-bold font-[Chivo] mb-2">{article.title}</h2>
            {article.summary && <p className="text-sm italic text-muted-foreground mb-4">{article.summary}</p>}
            <KbWalkthrough walkthrough={article.walkthrough} slug={article.slug} />
            <div className="space-y-1.5">{renderKbContent(article.content, { onOpenArticle: openArt })}</div>
            <div className="mt-5 pt-4 border-t border-border flex items-center gap-2" data-testid={tid("feedback")}>
              <span className="text-xs text-muted-foreground">Was this helpful?</span>
              <Button size="sm" variant={voted === "yes" ? "default" : "outline"} disabled={!!voted} onClick={() => vote(true)} data-testid={tid("vote-yes")}>👍 Yes</Button>
              <Button size="sm" variant={voted === "no" ? "destructive" : "outline"} disabled={!!voted} onClick={() => vote(false)} data-testid={tid("vote-no")}>👎 No</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid={tid("tab")} className="space-y-4 max-w-3xl">
      {intro && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <Lightbulb size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs">
            <p className="font-medium text-amber-900 dark:text-amber-200">{intro.title}</p>
            <p className="text-amber-800 dark:text-amber-300 mt-0.5">{intro.text}</p>
          </div>
        </div>
      )}
      <KbAskBox role={role} onOpen={openArt} testIdPrefix={testIdPrefix} />
      <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder} className="h-9 text-sm" data-testid={tid("search")} />
      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-8">Loading…</p>
      ) : data.sections.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8" data-testid={tid("empty")}>No articles found</p>
      ) : (
        <div className="space-y-3">
          {data.sections.map(section => (
            <div key={section.id} className="border border-border rounded-md bg-card" data-testid={tid(`section-${section.id}`)}>
              <div className="px-4 py-2.5 border-b border-border bg-muted/30">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{section.title}</h3>
              </div>
              <ul className="divide-y divide-border">
                {section.articles.map(a => (
                  <li key={a.slug}>
                    <button type="button" onClick={() => openArt(a.slug)}
                      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/50 text-left transition-colors"
                      data-testid={tid(`row-${a.slug}`)}>
                      {a.thumbnail ? <KbThumb src={a.thumbnail} size="sm" /> : <ArrowRight size={12} className="text-muted-foreground mt-1 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{a.title}</p>
                        {a.summary && <p className="text-[11px] text-muted-foreground mt-0.5">{a.summary}</p>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
